import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer,
} from 'recharts';
import ChartDefaults from '../../../shared/ChartDefaults';
import Store from '../../../shared/state/Store';
import Utils from '../../../shared/Utils';
import GraphUtils from '../../GraphUtils';
import CytoReducer from '../../ModelPreview/cytoscape/CytoReducer';
import ModellerDefaults from '../../ModellerDefaults';

const labelFormatter = (value) => ((value === 0) ? '' : `${value}%`);

const formatData = (data, decimalPlaces) => {
  const chartData = [];

  if (!data || data.length === 0) {
    return chartData;
  }

  for (let datasetIndex = 0; datasetIndex < data.length; datasetIndex += 1) {
    const dsData = data[datasetIndex];
    const dsKey = GraphUtils.convertDatasetIdToKey(dsData.dataset);
    for (let valueIndex = 0; valueIndex < dsData.resultValues.length; valueIndex += 1) {
      const { label, value } = dsData.resultValues[valueIndex];
      if (datasetIndex === 0) {
        chartData.push({
          name: label,
        });
      }
      chartData[valueIndex][dsKey] = Utils.roundFormat(value * 100, decimalPlaces, ChartDefaults.decimalPlaces);
    }
  }

  return chartData;
};

const calculateYAxisWidth = (data) => {
  let maxWidth = 0;
  data.forEach((item) => {
    const width = Utils.estimateTextWidth(item.name, ChartDefaults.BarChartStyleOptions.fontSize, ChartDefaults.BarChartStyleOptions.fontFamily, ChartDefaults.BarChartStyleOptions.fontWeight);
    maxWidth = Math.max(maxWidth, width);
  });
  return maxWidth;
};

const clipYAxisLabel = (value, maxWidth) => {
  if (!maxWidth) {
    return value;
  }
  const text = `${value ?? ''}`;
  if (!text) {
    return text;
  }
  const { fontSize, fontFamily, fontWeight } = ChartDefaults.BarChartStyleOptions;
  const fullWidth = Utils.estimateTextWidth(text, fontSize, fontFamily, fontWeight);
  if (fullWidth <= maxWidth) {
    return text;
  }

  if (maxWidth <= 0) {
    return '';
  }

  const ellipsis = '…';
  const ellipsisWidth = Utils.estimateTextWidth(ellipsis, fontSize, fontFamily, fontWeight);
  const availableWidth = Math.max(0, maxWidth - ellipsisWidth);
  if (availableWidth <= 0) {
    return ellipsis;
  }

  for (let i = text.length; i > 0; i -= 1) {
    const candidate = text.slice(0, i);
    const candidateWidth = Utils.estimateTextWidth(candidate, fontSize, fontFamily, fontWeight);
    if (candidateWidth <= availableWidth) {
      return `${candidate}${ellipsis}`;
    }
  }

  return ellipsis;
};

export default ({
  data,
  nodeConfig,
  chartRef,
  className,
  networkId,
  renderOffScreen = false,
  chartRefreshKey = 0,
  onChartRendered,
}) => {
  const [, dispatch] = Store.useStore();
  const nodeGraphics = nodeConfig.graphics || ModellerDefaults.DefaultGraphics;
  const chartData = formatData(data, (nodeGraphics.chart?.decimalPlaces !== undefined) ? nodeGraphics.chart.decimalPlaces : 2);
  // Use 100% height to fill the container, which may be adjusted for header offset
  const heightCss = '100%';
  const [svgWidth, setSvgWidth] = React.useState(undefined);

  // console.log('Barchart', nodeConfig.name, chartData);

  const margins = {};
  ['Left', 'Right', 'Top', 'Bottom'].forEach((p) => {
    const paddingKey = `padding${p}`;
    if (nodeGraphics.chart?.[paddingKey]) {
      margins[p.toLowerCase()] = parseInt(nodeGraphics.chart[paddingKey], 10);
    }
  });

  const [paddingBottomAuto, setPaddingBottomAuto] = React.useState(0);
  const [paddingLeftAuto, setPaddingLeftAuto] = React.useState(undefined);
  const [paddingRightAuto, setPaddingRightAuto] = React.useState(undefined);
  const paddingRightAutoRef = React.useRef(paddingRightAuto);
  const needsAutoMarginsRef = React.useRef(true);

  React.useEffect(() => {
    paddingRightAutoRef.current = paddingRightAuto;
  }, [paddingRightAuto]);

  React.useEffect(() => {
    if (!renderOffScreen) {
      return undefined;
    }
    // Reset auto margins on data refresh so they recompute cleanly.
    setPaddingBottomAuto(0);
    setPaddingLeftAuto(0);
    setPaddingRightAuto(0);
    needsAutoMarginsRef.current = true;
    return undefined;
  }, [renderOffScreen, chartRefreshKey]);

  const adjustMargins = (svg) => {
    if (!svg) {
      return;
    }
    if (!needsAutoMarginsRef.current) {
      return;
    }

    const yAxis = svg.getElementsByClassName('recharts-yAxis')[0];
    const xAxis = svg.getElementsByClassName('recharts-xAxis')[0];

    if (!xAxis || !yAxis) {
      return;
    }

    const canvasLeft = svg.getBoundingClientRect().x;
    const getLeftBound = (el) => {
      if (!el?.getBoundingClientRect) {
        return undefined;
      }
      let left = el.getBoundingClientRect().x;
      const bboxRect = Utils.getBoundsFromBBox(el);
      if (bboxRect && Number.isFinite(bboxRect.left)) {
        left = Math.min(left, bboxRect.left);
      }
      return left;
    };

    let minAxisLeft = Math.min(
      getLeftBound(yAxis) ?? canvasLeft,
      getLeftBound(xAxis) ?? canvasLeft,
    );
    const yAxisTicks = yAxis.getElementsByClassName('recharts-cartesian-axis-tick-value');
    [].forEach.call(yAxisTicks, (tick) => {
      const tickLeft = getLeftBound(tick);
      if (Number.isFinite(tickLeft)) {
        minAxisLeft = Math.min(minAxisLeft, tickLeft);
      }
    });
    const yAxisClipped = canvasLeft - minAxisLeft;
    if (renderOffScreen) {
      setPaddingLeftAuto(yAxisClipped + ChartDefaults.DefaultHorizontalPadding);
    } else {
      setPaddingLeftAuto(yAxisClipped);
    }

    const canvasBottom = svg.getBoundingClientRect().y + svg.getBoundingClientRect().height;
    if (xAxis) {
      const xAxisBottom = xAxis.getBoundingClientRect().y + xAxis.getBoundingClientRect().height;
      const xAxisClipped = xAxisBottom - canvasBottom;

      if (renderOffScreen) {
        // Offscreen rendering: use consistent calculation for all models
        // Calculate padding based on x-axis position relative to canvas
        const xAxisHeight = xAxis.getBoundingClientRect().height;
        const xAxisTop = xAxis.getBoundingClientRect().y;
        const canvasTop = svg.getBoundingClientRect().y;
        const canvasHeight = svg.getBoundingClientRect().height;

        // Calculate how much space the x-axis needs from the bottom of the canvas
        // xAxisBottom relative to canvas bottom = (xAxisTop + xAxisHeight) - (canvasTop + canvasHeight)
        const xAxisBottomRelativeToCanvas = (xAxisTop + xAxisHeight) - (canvasTop + canvasHeight);

        // Padding needed = x-axis height + any extension beyond canvas
        // If xAxisBottomRelativeToCanvas > 0, x-axis extends beyond, need extra padding
        // If xAxisBottomRelativeToCanvas <= 0, x-axis fits, just need x-axis height
        const neededPadding = xAxisHeight + Math.max(0, xAxisBottomRelativeToCanvas);

        // For offscreen, preserve the larger padding to avoid shrink on reflow
        setPaddingBottomAuto(Math.max(paddingBottomAuto, neededPadding));
      } else if (xAxisClipped > 0) {
        // Normal rendering: x-axis extends beyond canvas bottom
        setPaddingBottomAuto(paddingBottomAuto + xAxisClipped);
      }
    }

    const canvasRight = svg.getBoundingClientRect().x + svg.getBoundingClientRect().width;
    const currentPaddingRight = renderOffScreen
      ? (paddingRightAutoRef.current || 0)
      : (margins.right || 0);
    let deltaRight = 0;
    const extendRightPadding = (el) => {
      if (!el?.getBoundingClientRect) {
        return;
      }
      const clientRect = el.getBoundingClientRect();
      const clientRight = clientRect.x + clientRect.width;
      const clientDelta = currentPaddingRight + clientRight - canvasRight;
      if (Number.isFinite(clientDelta)) {
        deltaRight = Math.max(deltaRight, clientDelta);
      }

      const bboxRect = Utils.getBoundsFromBBox(el);
      if (bboxRect) {
        const bboxRight = bboxRect.x + bboxRect.width;
        const bboxDelta = currentPaddingRight + bboxRight - canvasRight;
        if (Number.isFinite(bboxDelta)) {
          deltaRight = Math.max(deltaRight, bboxDelta);
        }
      }
    };

    [].forEach.call(svg.getElementsByClassName('recharts-bar'), extendRightPadding);
    if (renderOffScreen) {
      [].forEach.call(svg.getElementsByClassName('recharts-label'), extendRightPadding);
      [].forEach.call(svg.getElementsByClassName('recharts-text'), extendRightPadding);
    }

    if (renderOffScreen) {
      const nextRight = (Number.isFinite(deltaRight) && deltaRight > 0)
        ? Math.max(currentPaddingRight, deltaRight)
        : currentPaddingRight;
      setPaddingRightAuto((prev) => {
        const prevRight = prev || 0;
        const nextPadding = Math.max(prevRight, nextRight);
        paddingRightAutoRef.current = nextPadding;
        return nextPadding + ChartDefaults.DefaultHorizontalPadding;
      });
    } else if (!Number.isFinite(deltaRight) || deltaRight < 0) {
      setPaddingRightAuto(undefined);
    } else {
      setPaddingRightAuto(deltaRight);
    }
    needsAutoMarginsRef.current = false;
  };

  React.useEffect(() => {
    setTimeout(() => {
      if (data && data.length > 0) {
        const svg = Utils.getSvg(chartRef);
        if (svg) {
          svg.style.background = 'white';
          adjustMargins(svg);
        }
      }
    }, 10);
  }, [data[0]?.refreshedAt, nodeConfig.graphics?.chart, chartRefreshKey]);

  React.useEffect(() => {
    if (!renderOffScreen) {
      return undefined;
    }
    const timeoutId = setTimeout(() => {
      const svg = Utils.getSvg(chartRef);
      if (!svg) {
        return;
      }
      try {
        dispatch(CytoReducer.updateNodeChart(chartRef, networkId, nodeConfig.id));
        if (typeof onChartRendered === 'function') {
          onChartRendered(networkId, nodeConfig.id, chartRefreshKey);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[BarChart] updateNodeChart: Error dispatching updateNodeChart', {
          networkId,
          nodeId: nodeConfig.id,
          error: error.message,
          stack: error.stack,
        });
      }
    }, 10);
    return () => clearTimeout(timeoutId);
  }, [
    renderOffScreen,
    chartRefreshKey,
  ]);

  React.useEffect(() => {
    const container = chartRef?.current;
    if (!container) return undefined;

    const update = () => {
      // SVG might not exist yet on first render
      const svg = Utils.getSvg(chartRef);
      if (!svg) return;

      const rect = svg.getBoundingClientRect();
      if (Number.isFinite(rect.width) && rect.width > 0) {
        setSvgWidth(rect.width);
      }
    };

    // Initial attempt + a couple of rAF retries to catch SVG insertion
    update();
    const raf1 = requestAnimationFrame(update);
    const raf2 = requestAnimationFrame(update);

    const ro = new ResizeObserver(() => update());
    ro.observe(container); // observe container, not svg

    return () => {
      ro.disconnect();
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [chartRef]);

  const yAxisWidth = React.useMemo(() => {
    if (!svgWidth || !chartData?.length) {
      return undefined;
    }
    const requiredWidth = calculateYAxisWidth(chartData);
    const maxWidth = Math.min(requiredWidth, svgWidth * 0.5);
    return maxWidth;
  }, [chartData, svgWidth]);

  // For offscreen rendering, always use calculated padding (ignore existing padding)
  // For normal rendering, use existing padding if configured, otherwise use calculated
  if (renderOffScreen) {
    margins.bottom = paddingBottomAuto;
  } else {
    margins.bottom = margins.bottom !== undefined ? margins.bottom : paddingBottomAuto;
  }
  const leftMargin = paddingLeftAuto;
  margins.left = margins.left !== undefined ? margins.left : leftMargin;
  margins.right = margins.right !== undefined ? margins.right : paddingRightAuto;

  // Add extra right padding for offscreen labels, avoid forcing left padding.
  if (renderOffScreen) {
    if (!Number.isFinite(margins.left)) {
      margins.left = 0;
    }
    if (!Number.isFinite(margins.right)) {
      margins.right = 0;
    }
  }

  // Calculate maximum value from chart data for dynamic X-axis domain
  let maxValue = 0;
  if (chartData && chartData.length > 0) {
    chartData.forEach((item) => {
      Object.keys(item).forEach((key) => {
        if (key !== 'name' && typeof item[key] === 'number') {
          maxValue = Math.max(maxValue, item[key]);
        }
      });
    });
  }
  // Set domain to min(100, maxValue + 10), but at least 10
  const xDomainMax = Math.min(100, Math.max(10, maxValue));

  return (
    <div
      style={{
        width: '100%',
        height: heightCss,
        background: 'white',
      }}
      ref={chartRef}
      className={className}
    >
      <ResponsiveContainer>
        <BarChart
          margin={margins}
          data={chartData}
          layout="vertical"
          style={{
            fontFamily: ChartDefaults.BarChartStyleOptions.fontFamily,
          }}
        >
          <XAxis type="number" domain={[0, xDomainMax]} style={{ fontSize: ChartDefaults.BarChartStyleOptions.fontSize }} />
          <YAxis
            type="category"
            dataKey="name"
            interval={0}
            style={{ fontSize: ChartDefaults.BarChartStyleOptions.fontSize }}
            width={yAxisWidth}
            tickFormatter={(value) => clipYAxisLabel(value, yAxisWidth)}
          />

          {
              data.map((dsData, dsIndex) => (
                <Bar
                  key={dsData.dataset}
                  isAnimationActive={false}
                  dataKey={GraphUtils.convertDatasetIdToKey(dsData.dataset)}
                  fill={ChartDefaults.Colors[dsIndex]}
                  label={{
                    position: 'right',
                    formatter: labelFormatter,
                    style: { fontSize: ChartDefaults.BarChartStyleOptions.fontSize, fontWeight: ChartDefaults.BarChartStyleOptions.fontWeight },
                  }}
                />
              ))
            }
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};
